import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Heart, TrendingUp, TrendingDown, Minus } from "lucide-react";

const BAND_TONE = {
  green: { fg: "#34C759", bg: "rgba(52,199,89,0.10)", ring: "rgba(52,199,89,0.35)", label: "Healthy" },
  amber: { fg: "#FF9F0A", bg: "rgba(255,159,10,0.10)", ring: "rgba(255,159,10,0.35)", label: "Needs attention" },
  red:   { fg: "#FF3B30", bg: "rgba(255,59,48,0.10)",  ring: "rgba(255,59,48,0.35)",  label: "Critical" },
};

/**
 * Ops Health Score — top-of-dashboard signal blending on-time-rate,
 * geofence-breach density, and breach response time into a single 0-100.
 * Colour-coded green/amber/red so executives see the state at a glance.
 */
export default function OpsHealthBadge({ cadence: cadenceProp, onCadenceChange, initialCadence = "7d" }) {
  // Controlled mode when both `cadence` + `onCadenceChange` are passed;
  // otherwise falls back to internal state seeded by `initialCadence`.
  const [innerCadence, setInnerCadence] = useState(cadenceProp || initialCadence);
  const cadence = cadenceProp || innerCadence;
  const setCadence = onCadenceChange || setInnerCadence;
  const [health, setHealth] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [now, hist] = await Promise.all([
        api.get(`/ops-health?cadence=${cadence}`),
        // Only spark a 7-day history on the 7d cadence — for wider 30d/90d
        // windows a rolling-7-day trend under the sub-scores is misleading.
        cadence === "7d"
          ? api.get(`/ops-health/history?days=7`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setHealth(now.data);
      setHistory(hist.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unavailable");
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, [cadence]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  if (loading && !health) {
    return (
      <section className="border border-[#27272A] bg-[#121212] px-6 py-5" data-testid="ops-health-card">
        <div className="label-mute">Ops Health Score</div>
        <div className="mt-2 text-zinc-500 text-sm scanlines">Loading…</div>
      </section>
    );
  }
  if (err || !health) {
    return (
      <section className="border border-[#27272A] bg-[#121212] px-6 py-5" data-testid="ops-health-card">
        <div className="label-mute">Ops Health Score</div>
        <div className="mt-2 text-zinc-500 text-sm">Unavailable ({err || "no data"})</div>
      </section>
    );
  }

  const tone = BAND_TONE[health.band] || BAND_TONE.amber;
  const TrendIcon = health.score >= 85 ? TrendingUp : health.score >= 60 ? Minus : TrendingDown;

  return (
    <section
      className="border bg-[#121212] px-6 py-5"
      style={{ borderColor: tone.ring }}
      data-testid="ops-health-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Big score */}
        <div className="flex items-center gap-5">
          <div
            className="size-24 rounded-full border-2 flex flex-col items-center justify-center flex-none"
            style={{ background: tone.bg, borderColor: tone.ring }}
            data-testid="ops-health-score-ring"
          >
            <div className="font-display font-bold text-4xl leading-none" style={{ color: tone.fg }}>
              {health.score}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">/ 100</div>
          </div>
          <div>
            <div className="label-mute flex items-center gap-2" style={{ color: tone.fg }}>
              <Heart className="size-3" /> Ops Health Score
            </div>
            <div className="font-display font-bold text-xl uppercase tracking-tight mt-1 flex items-center gap-2" data-testid="ops-health-band">
              <TrendIcon className="size-4" style={{ color: tone.fg }} />
              <span style={{ color: tone.fg }}>{tone.label}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              {health.total_shifts} shifts · {health.no_shows} no-shows · {health.tardies} tardies · {health.open_breaches} open breaches
            </div>
          </div>
        </div>

        {/* Sub-scores + cadence switcher */}
        <div className="flex items-center gap-5 flex-wrap">
          <SubScore label="On-time" value={health.on_time_score} testId="ops-health-sub-on-time" to="/tardies" spark={history?.points?.map((p) => p.on_time_score)} sparkTestId="ops-health-spark-on-time" />
          <SubScore label="Fence" value={health.breach_score} testId="ops-health-sub-fence" to="/geofence" spark={history?.points?.map((p) => p.breach_score)} sparkTestId="ops-health-spark-fence" />
          <SubScore label="Response" value={health.response_score} testId="ops-health-sub-response" to="/geofence" spark={history?.points?.map((p) => p.response_score)} sparkTestId="ops-health-spark-response" />
          <div className="inline-flex items-center gap-1 self-start">
            {["7d", "30d", "90d"].map((c) => (
              <button
                key={c}
                data-testid={`ops-health-cadence-${c}`}
                onClick={() => setCadence(c)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border ${cadence === c ? "text-white border-[#0A84FF]" : "border-[#27272A] text-zinc-400 hover:text-zinc-200"}`}
              >
                {c === "7d" ? "7D" : c === "30d" ? "30D" : "90D"}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Sparkline({ values, tone, testId }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const W = 80;
  const H = 22;
  const max = 100; // scores are always 0-100 → fixed scale keeps trends comparable
  const min = 0;
  const step = W / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = H - ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = values[values.length - 1];
  const lastX = (values.length - 1) * step;
  const lastY = H - ((last - min) / (max - min)) * H;
  return (
    <svg
      width={W} height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="mt-1"
      data-testid={testId}
      aria-hidden
    >
      <polyline
        points={pts}
        fill="none"
        stroke={tone}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.85}
      />
      <circle cx={lastX} cy={lastY} r={2} fill={tone} />
    </svg>
  );
}

function SubScore({ label, value, testId, to, spark, sparkTestId }) {
  const tone = value >= 85 ? "#34C759" : value >= 60 ? "#FF9F0A" : "#FF3B30";
  const body = (
    <div className="min-w-[80px]">
      <div className="label-mute">{label}</div>
      <div className="font-display font-bold text-2xl mt-0.5" style={{ color: tone }} data-testid={testId}>
        {value}
        <span className="text-xs text-zinc-500 font-normal ml-1">/100</span>
      </div>
      <Sparkline values={spark} tone={tone} testId={sparkTestId} />
    </div>
  );
  return to ? <Link to={to} className="hover:opacity-80 transition-opacity">{body}</Link> : body;
}
